import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Chip,
  CircularProgress,

  useTheme,
  alpha,
  Tooltip,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  MdAccessTime,
  MdLogin,
  MdLogout,
  MdArrowForward,
  MdCheckCircle,
  MdLocalCafe,
  MdRestaurant,
  MdPauseCircle,
  MdPlayCircle,
  MdTimer,
} from "react-icons/md";
import {
  clockIn,
  clockOut,
  getTodayStatus,
  startBreak,
  endBreak,
} from "services/company/attendanceManagement/attendanceRecords/attendanceRecordsService";
import { showSuccessToast, showErrorToast } from "utils/toastNotification";
import usePermissions from "hooks/usePermissions";
import { selectCurrentUser } from "store/features/auth/authSelectors";
import { getSecureLocalItem } from "utils/secureSessionStorage";
import useDesktopActivityTracker from "hooks/useDesktopActivityTracker";

const MyAttendanceCard = ({ myTodayRecord, user, onRefresh, onStatusUpdate }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === "dark";

  // Permissions & Role
  const { hasPermission, permissions } = usePermissions();
  const currentUser = useSelector(selectCurrentUser) || user;
  const userRole = getSecureLocalItem("role") || currentUser?.role || currentUser?.role_name || user?.role || "Company";
  const isCompanyRole = userRole?.toLowerCase() === "company";
  const isEmployeeRole = userRole?.toLowerCase() === "employee";

  const canViewAttendanceRecords =
    hasPermission("attendance_records.view") ||
    hasPermission("attendance.view") ||
    (permissions && permissions.some((p) => typeof p === "string" && p.startsWith("attendance"))) ||
    !isEmployeeRole;

  const [actionLoading, setActionLoading] = useState(false);
  const [breakLoading, setBreakLoading] = useState(null); // 'tea_break' | 'lunch_break' | 'other_break' | 'end' | null
  const [personalStatus, setPersonalStatus] = useState(null);
  const [elapsedBreakSeconds, setElapsedBreakSeconds] = useState(0);

  const [liveCurrentTime, setLiveCurrentTime] = useState(() => new Date());


  useEffect(() => {
    const clockTimer = setInterval(() => {
      setLiveCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(clockTimer);
  }, []);

  // Fetch today's attendance status from GET /v1/api/attendance/today-status
  const fetchPersonalTodayStatus = useCallback(async () => {
    try {
      const res = await getTodayStatus();
      if (res?.meta?.status === true || res?.status === 200) {
        const data = res.data || res;
        setPersonalStatus(data);
      }
    } catch (err) {
      console.warn("Failed to fetch today status:", err);
    }
  }, []);

  useEffect(() => {
    const token = getSecureLocalItem("token");
    if (token && !isCompanyRole) {
      fetchPersonalTodayStatus();
    }
  }, [fetchPersonalTodayStatus, isCompanyRole]);

  // Active break info from personal status
  const activeBreak = personalStatus?.active_break || null;
  const isOnBreak = Boolean(activeBreak);
  const totalBreakMinutes = personalStatus?.break_minutes ?? myTodayRecord?.break_minutes ?? 0;
  const hasActiveTask = Boolean(personalStatus?.has_active_task || personalStatus?.active_task);
  const activeTask = personalStatus?.active_task || null;

  // Live timer for ongoing break
  useEffect(() => {
    if (!activeBreak?.start_time) {
      setElapsedBreakSeconds(0);
      return;
    }

    const calcElapsed = () => {
      const startMs = new Date(activeBreak.start_time).getTime();
      const nowMs = Date.now();
      const diffSec = Math.max(0, Math.floor((nowMs - startMs) / 1000));
      setElapsedBreakSeconds(diffSec);
    };

    calcElapsed();
    const interval = setInterval(calcElapsed, 1000);
    return () => clearInterval(interval);
  }, [activeBreak]);

  // Combine personal status from API with prop record
  const activeRecord = personalStatus?.check_in
    ? personalStatus
    : myTodayRecord || personalStatus;

  const isCheckedIn = Boolean(activeRecord?.check_in && !activeRecord?.check_out);
  const isCheckedOut = Boolean(activeRecord?.check_in && activeRecord?.check_out);
  const notPunched = !activeRecord?.check_in;

  // Desktop activity tracker status
  const trackerState = useDesktopActivityTracker({ enabled: isCheckedIn });

  // Periodic polling & event listeners to sync task status
  useEffect(() => {
    const handleTaskChange = () => {
      fetchPersonalTodayStatus();
    };
    window.addEventListener("task_status_changed", handleTaskChange);
    window.addEventListener("focus", handleTaskChange);

    let pollInterval = null;
    if (isCheckedIn) {
      pollInterval = setInterval(fetchPersonalTodayStatus, 15000);
    }

    return () => {
      window.removeEventListener("task_status_changed", handleTaskChange);
      window.removeEventListener("focus", handleTaskChange);
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [isCheckedIn, fetchPersonalTodayStatus]);

  // Notify parent dashboard component of status changes
  useEffect(() => {
    if (onStatusUpdate && personalStatus) {
      onStatusUpdate(personalStatus);
    }
  }, [personalStatus, onStatusUpdate]);

  if (isCompanyRole) {
    return null;
  }

  const formatTime = (dateStr) => {
    if (!dateStr) return "--:--";
    return new Date(dateStr).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };



  const formatTimerString = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    const hrs = Math.floor(mins / 60);
    const remMins = mins % 60;
    if (hrs > 0) {
      return `${String(hrs).padStart(2, "0")}:${String(remMins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    }
    return `${String(remMins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  // Helper to obtain GPS coordinates or fallback
  const getCoordinates = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ latitude: 0, longitude: 0 });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          });
        },
        () => {
          resolve({ latitude: 0, longitude: 0 });
        },
        { timeout: 5000 }
      );
    });
  };

  const handleCheckIn = async () => {
    setActionLoading(true);
    try {
      const coords = await getCoordinates();
      const response = await clockIn(coords);

      if (response?.meta?.status === true || response?.status === 200) {
        showSuccessToast(response?.meta?.message || "Checked in successfully!");
        await fetchPersonalTodayStatus();
        if (onRefresh) onRefresh();
      } else {
        const errorMsg = response?.meta?.message || "Check-in failed";
        showErrorToast(errorMsg);
      }
    } catch (err) {
      console.error("Check-in error:", err);
      showErrorToast(err?.meta?.message || err?.message || "Check-in failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckOut = async () => {
    setActionLoading(true);
    try {
      const coords = await getCoordinates();
      const response = await clockOut(coords);

      if (response?.meta?.status === true || response?.status === 200) {
        showSuccessToast(response?.meta?.message || "Checked out successfully!");
        window.dispatchEvent(new Event("task_status_changed"));
        await fetchPersonalTodayStatus();
        if (onRefresh) onRefresh();
      } else {
        const errorMsg = response?.meta?.message || "Check-out failed";
        showErrorToast(errorMsg);
      }
    } catch (err) {
      console.error("Check-out error:", err);
      showErrorToast(err?.meta?.message || err?.message || "Check-out failed");
    } finally {
      setActionLoading(false);
    }
  };

  // Break handlers
  const handleStartBreak = async (breakType, customRemark) => {
    if (!isCheckedIn) {
      showErrorToast("Please check in first before taking a break.");
      return;
    }
    if (isOnBreak) {
      showErrorToast("You already have an active break in progress. End it first.");
      return;
    }

    setBreakLoading(breakType);
    try {
      const breakLabel = breakType.replace("_", " ").toUpperCase();
      const response = await startBreak({
        break_type: breakType,
        remarks: customRemark || `Started ${breakLabel} from dashboard`,
      });
      if (response?.meta?.status === true || response?.status === 200) {
        showSuccessToast(response?.meta?.message || `${breakLabel} started! Enjoy your break.`);
        await fetchPersonalTodayStatus();
        if (onRefresh) onRefresh();
      } else {
        showErrorToast(response?.meta?.message || "Failed to start break");
      }
    } catch (err) {
      console.error("Start break error:", err);
      showErrorToast(err?.meta?.message || err?.message || "Failed to start break");
    } finally {
      setBreakLoading(null);
    }
  };

  const handleEndBreak = async () => {
    setBreakLoading("end");
    try {
      const response = await endBreak();
      if (response?.meta?.status === true || response?.status === 200) {
        showSuccessToast(response?.meta?.message || "Break ended! Welcome back to work.");
        await fetchPersonalTodayStatus();
        if (onRefresh) onRefresh();
      } else {
        showErrorToast(response?.meta?.message || "Failed to end break");
      }
    } catch (err) {
      console.error("End break error:", err);
      showErrorToast(err?.meta?.message || err?.message || "Failed to end break");
    } finally {
      setBreakLoading(null);
    }
  };

  const breakButtonsConfig = [
    {
      id: "tea_break",
      label: "Tea Break",
      icon: <MdLocalCafe size={18} />,
      activeColor: "#f59e0b",
      gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      lightBg: "#fef3c7",
    },
    {
      id: "lunch_break",
      label: "Lunch Break",
      icon: <MdRestaurant size={18} />,
      activeColor: "#f97316",
      gradient: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
      lightBg: "#ffedd5",
    },
    {
      id: "other_break",
      label: "Other Break",
      icon: <MdPauseCircle size={18} />,
      activeColor: "#6366f1",
      gradient: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
      lightBg: "#e0e7ff",
    },
  ];

  const getStatusChipConfig = () => {
    if (isOnBreak) {
      const label =
        activeBreak?.break_type === "tea_break"
          ? "On Tea Break"
          : activeBreak?.break_type === "lunch_break"
          ? "On Lunch Break"
          : "On Break";
      return {
        label,
        bg: isDark ? alpha("#f59e0b", 0.2) : "#fef3c7",
        color: "#d97706",
      };
    }
    if (isCheckedIn) {
      return {
        label: "Checked In",
        bg: isDark ? alpha("#10b981", 0.2) : "#d1fae5",
        color: "#059669",
      };
    }
    if (isCheckedOut) {
      return {
        label: "Shift Ended",
        bg: isDark ? alpha("#6366f1", 0.2) : "#e0e7ff",
        color: "#4338ca",
      };
    }
    return {
      label: "Not Checked In",
      bg: isDark ? alpha("#f43f5e", 0.2) : "#ffe4e6",
      color: "#e11d48",
    };
  };

  const formatLiveClock = () => {
    return liveCurrentTime.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const getTimezoneString = () => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
      const offsetMinutes = -new Date().getTimezoneOffset();
      const sign = offsetMinutes >= 0 ? "+" : "-";
      const hours = String(Math.floor(Math.abs(offsetMinutes) / 60)).padStart(2, "0");
      const mins = String(Math.abs(offsetMinutes) % 60).padStart(2, "0");
      return `${tz} (UTC ${sign}${hours}:${mins})`;
    } catch {
      return "Local Timezone";
    }
  };

  const calculateActiveHoursShort = () => {
    if (!activeRecord?.check_in) return "00h 00m";
    if (activeRecord?.working_minutes !== undefined && activeRecord?.working_minutes !== null && activeRecord?.working_minutes > 0) {
      const hrs = Math.floor(activeRecord.working_minutes / 60);
      const mins = activeRecord.working_minutes % 60;
      return `${String(hrs).padStart(2, "0")}h ${String(mins).padStart(2, "0")}m`;
    }
    const startTime = new Date(activeRecord.check_in).getTime();
    const endTime = activeRecord.check_out ? new Date(activeRecord.check_out).getTime() : Date.now();
    const diffMs = Math.max(0, endTime - startTime);
    const totalMins = Math.floor(diffMs / (1000 * 60));
    const netMins = Math.max(0, totalMins - (totalBreakMinutes || 0));
    const hrs = Math.floor(netMins / 60);
    const mins = netMins % 60;
    return `${String(hrs).padStart(2, "0")}h ${String(mins).padStart(2, "0")}m`;
  };

  const statusChip = getStatusChipConfig();

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: "8px",
        bgcolor: isDark ? "#111827" : "#ffffff",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"}`,
        boxShadow: isDark
          ? "0 1px 3px rgba(0,0,0,0.4)"
          : "0 1px 3px rgba(15,23,42,0.03)",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <Box>
        {/* Header Title & Live Status Chip */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            pb: 1.5,
            borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9"}`,
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "4px",
                bgcolor: isDark ? alpha("#4f46e5", 0.2) : "#eef2ff",
                color: "#4f46e5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MdAccessTime size={18} />
            </Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                fontSize: "1rem",
                letterSpacing: "-0.01em",
              }}
            >
              Time & Attendance
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            {trackerState.isConnected && (
              <Tooltip
                title={`Desktop Activity Tracker Connected. System Idle: ${trackerState.idleSeconds}s / ${trackerState.thresholdSeconds}s`}
              >
                <Chip
                  size="small"
                  label={trackerState.isActive ? "Tracker Active" : `Idle (${trackerState.idleSeconds}s)`}
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.7rem",
                    height: 22,
                    borderRadius: "4px",
                    bgcolor: trackerState.isActive
                      ? isDark
                        ? alpha("#10b981", 0.2)
                        : "#ecfdf5"
                      : isDark
                      ? alpha("#f43f5e", 0.2)
                      : "#ffe4e6",
                    color: trackerState.isActive ? "#059669" : "#e11d48",
                  }}
                />
              </Tooltip>
            )}

            <Box
              component="span"
              sx={{
                fontSize: "0.72rem",
                fontWeight: 700,
                px: 1.2,
                py: 0.35,
                borderRadius: "4px",
                bgcolor: statusChip.bg,
                color: statusChip.color,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: statusChip.color,
                }}
              />
              {statusChip.label}
            </Box>
          </Stack>
        </Box>

        {/* Station Standard Time Section */}
        <Box
          sx={{
            py: 2,
            px: 2,
            mb: 2,
            borderRadius: "6px",
            bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9"}`,
            textAlign: "center",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: "block",
              fontWeight: 700,
              color: "text.secondary",
              letterSpacing: "1.2px",
              textTransform: "uppercase",
              fontSize: "0.68rem",
              mb: 0.5,
            }}
          >
            Station Standard Time
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: "text.primary",
              letterSpacing: "-0.02em",
              fontSize: { xs: "1.75rem", sm: "2.1rem" },
              lineHeight: 1.1,
              fontFamily: "monospace",
              mb: 0.5,
            }}
          >
            {formatLiveClock()}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            {getTimezoneString()}
          </Typography>

          {/* 3 Metrics: CHECK-IN, CHECK-OUT, ACTIVE HOURS */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 1.5,
              mt: 2,
              pt: 1.5,
              borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
            }}
          >
            {/* Check-In */}
            <Box
              sx={{
                p: 1.25,
                borderRadius: "4px",
                bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  fontWeight: 700,
                  fontSize: "0.65rem",
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Check-In
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  fontSize: "0.88rem",
                  color: "text.primary",
                  fontFamily: "monospace",
                  mt: 0.25,
                }}
              >
                {formatTime(activeRecord?.check_in)}
              </Typography>
            </Box>

            {/* Check-Out */}
            <Box
              sx={{
                p: 1.25,
                borderRadius: "4px",
                bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  fontWeight: 700,
                  fontSize: "0.65rem",
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Check-Out
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  fontSize: "0.88rem",
                  color: "text.primary",
                  fontFamily: "monospace",
                  mt: 0.25,
                }}
              >
                {formatTime(activeRecord?.check_out)}
              </Typography>
            </Box>

            {/* Active Hours */}
            <Box
              sx={{
                p: 1.25,
                borderRadius: "4px",
                bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  fontWeight: 700,
                  fontSize: "0.65rem",
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Active Hours
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 800,
                  fontSize: "0.88rem",
                  color: "#2563eb",
                  fontFamily: "monospace",
                  mt: 0.25,
                }}
              >
                {calculateActiveHoursShort()}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* =====================================================
            ACTIVE BREAK LIVE BANNER
        ===================================================== */}
        <AnimatePresence>
          {isOnBreak && (
            <motion.div
              initial={{ opacity: 0, scale: 0.96, height: 0 }}
              animate={{ opacity: 1, scale: 1, height: "auto" }}
              exit={{ opacity: 0, scale: 0.96, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 2.5,
                  borderRadius: 1,
                  bgcolor: isDark ? alpha("#f59e0b", 0.15) : "#fef3c7",
                  border: 1,
                  borderColor: isDark ? alpha("#f59e0b", 0.3) : "#fde68a",
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: "#f59e0b",
                        boxShadow: "0 0 8px #f59e0b",
                        animation: "pulse 1.5s infinite ease-in-out",
                        "@keyframes pulse": {
                          "0%": { opacity: 0.4, transform: "scale(0.85)" },
                          "50%": { opacity: 1, transform: "scale(1.15)" },
                          "100%": { opacity: 0.4, transform: "scale(0.85)" },
                        },
                      }}
                    />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: isDark ? "#fbbf24" : "#92400e" }}>
                      {activeBreak?.break_type === "tea_break"
                        ? "Currently on Tea Break"
                        : activeBreak?.break_type === "lunch_break"
                        ? "Currently on Lunch Break"
                        : "Currently on Other Break"}
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <MdTimer size={16} color="#d97706" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, fontFamily: "monospace", color: "#d97706" }}>
                      {formatTimerString(elapsedBreakSeconds)}
                    </Typography>
                  </Stack>
                </Stack>

                <Button
                  variant="contained"
                  fullWidth
                  size="medium"
                  disabled={breakLoading === "end"}
                  onClick={handleEndBreak}
                  startIcon={breakLoading === "end" ? <CircularProgress size={16} color="inherit" /> : <MdPlayCircle size={18} />}
                  sx={{
                    borderRadius: 1,
                    textTransform: "none",
                    fontWeight: 800,
                    py: 1,
                    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    color: "#ffffff",
                    boxShadow: "0 3px 10px rgba(16, 185, 129, 0.3)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                    },
                  }}
                >
                  {breakLoading === "end" ? "Ending Break..." : "End Break & Resume Work"}
                </Button>
              </Paper>
            </motion.div>
          )}
        </AnimatePresence>

        {/* =====================================================
            ACTIVE TASK BADGE (When an active task is running)
        ===================================================== */}
        <AnimatePresence>
          {!isOnBreak && hasActiveTask && activeTask && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  mb: 2.5,
                  borderRadius: 1.5,
                  bgcolor: isDark ? alpha("#10b981", 0.12) : "#f0fdf4",
                  border: 1,
                  borderColor: isDark ? alpha("#10b981", 0.3) : "#bbf7d0",
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1, mr: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "#10b981",
                        boxShadow: "0 0 6px #10b981",
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: "#059669",
                          display: "block",
                          fontSize: "0.7rem",
                          textTransform: "uppercase",
                        }}
                      >
                        Active Task in Progress
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          color: "text.primary",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {activeTask.title}
                      </Typography>
                    </Box>
                  </Stack>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => navigate("/company/tasks")}
                    sx={{ textTransform: "none", fontWeight: 700, fontSize: "0.75rem", color: "#059669" }}
                  >
                    View
                  </Button>
                </Stack>
              </Paper>
            </motion.div>
          )}
        </AnimatePresence>

        {/* =====================================================
            3 BREAK BUTTONS (Only for Employees)
        ===================================================== */}
        {isEmployeeRole && (
          <Box sx={{ mb: 2.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Break Options
              </Typography>
              {!isCheckedIn && (
                <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.7rem" }}>
                  (Punch in to enable)
                </Typography>
              )}
            </Stack>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 1.5,
                width: "100%",
              }}
            >
              {breakButtonsConfig.map((item) => {
                const isThisBreakActive = activeBreak?.break_type === item.id;
                const isButtonDisabled = !isCheckedIn || isCheckedOut || (isOnBreak && !isThisBreakActive) || Boolean(breakLoading);
                const isLoadingThis = breakLoading === item.id;

                return (
                  <Tooltip
                    key={item.id}
                    title={
                      !isCheckedIn
                        ? "You must punch in first"
                        : isCheckedOut
                        ? "Shift completed for today"
                        : isOnBreak && !isThisBreakActive
                        ? "End your active break first"
                        : `Start ${item.label}`
                    }
                    arrow
                  >
                    <Box sx={{ width: "100%" }}>
                      <Button
                        variant={isThisBreakActive ? "contained" : "outlined"}
                        fullWidth
                        disabled={isButtonDisabled}
                        onClick={() => {
                          if (isThisBreakActive) {
                            handleEndBreak();
                          } else {
                            handleStartBreak(item.id);
                          }
                        }}
                        sx={{
                          width: "100%",
                          py: 1.3,
                          px: 1,
                          minHeight: 64,
                          borderRadius: 1.5,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.6,
                          textTransform: "none",
                          fontSize: "0.78rem",
                          fontWeight: 700,
                          borderColor: isThisBreakActive
                            ? item.activeColor
                            : isDark
                            ? alpha(item.activeColor, 0.3)
                            : alpha(item.activeColor, 0.4),
                          color: isThisBreakActive
                            ? "#ffffff"
                            : isDark
                            ? alpha(item.activeColor, 0.95)
                            : item.activeColor,
                          background: isThisBreakActive
                            ? item.gradient
                            : isDark
                            ? alpha(item.activeColor, 0.08)
                            : item.lightBg,
                          boxShadow: isThisBreakActive ? `0 4px 12px ${alpha(item.activeColor, 0.4)}` : "none",
                          "&:hover": {
                            borderColor: item.activeColor,
                            background: isThisBreakActive
                              ? item.gradient
                              : alpha(item.activeColor, 0.15),
                          },
                          "&.Mui-disabled": {
                            opacity: 0.55,
                          },
                        }}
                      >
                        {isLoadingThis ? (
                          <CircularProgress size={18} sx={{ color: item.activeColor, my: 0.2 }} />
                        ) : (
                          item.icon
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            lineHeight: 1.1,
                            textAlign: "center",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.label}
                        </Typography>
                      </Button>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>

      {/* Check In / Check Out Interactive Action Button */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
        {notPunched && (
          <Button
            variant="contained"
            fullWidth
            size="large"
            disabled={actionLoading}
            onClick={handleCheckIn}
            startIcon={actionLoading ? <CircularProgress size={18} color="inherit" /> : <MdLogin />}
            sx={{
              borderRadius: 1,
              textTransform: "none",
              fontWeight: 800,
              py: 1.2,
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              boxShadow: "0 4px 14px rgba(16, 185, 129, 0.35)",
              color: "#ffffff",
              "&:hover": {
                background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                boxShadow: "0 6px 18px rgba(16, 185, 129, 0.45)",
              },
            }}
          >
            {actionLoading ? "Punching In..." : "Check In Now"}
          </Button>
        )}

        {isCheckedIn && (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.25 }}>
            <Button
              variant="outlined"
              size="medium"
              disabled={actionLoading || isOnBreak}
              onClick={() => {
                if (breakButtonsConfig && breakButtonsConfig.length > 0) {
                  handleStartBreak(breakButtonsConfig[0].id);
                }
              }}
              startIcon={<MdLocalCafe size={18} />}
              sx={{
                borderRadius: "6px",
                textTransform: "none",
                fontWeight: 700,
                py: 1.1,
                borderColor: isDark ? "rgba(255,255,255,0.12)" : "#cbd5e1",
                color: isDark ? "#f1f5f9" : "#1e293b",
                bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
                "&:hover": {
                  borderColor: "#4f46e5",
                  bgcolor: isDark ? alpha("#4f46e5", 0.1) : "#eef2ff",
                  color: "#4f46e5",
                },
              }}
            >
              Take Break
            </Button>

            <Button
              variant="contained"
              size="medium"
              disabled={actionLoading || isOnBreak}
              onClick={handleCheckOut}
              startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <MdLogout size={18} />}
              sx={{
                borderRadius: "6px",
                textTransform: "none",
                fontWeight: 700,
                py: 1.1,
                bgcolor: "#dc2626",
                color: "#ffffff",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "#b91c1c",
                },
              }}
            >
              {actionLoading
                ? "Punching Out..."
                : isOnBreak
                ? "End Break First"
                : "Clock Out"}
            </Button>
          </Box>
        )}

        {isCheckedOut && (
          <Button
            variant="outlined"
            fullWidth
            disabled
            startIcon={<MdCheckCircle color="#6366f1" />}
            sx={{
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 700,
              py: 1,
              borderColor: isDark ? "rgba(99, 102, 241, 0.3)" : "#c7d2fe",
              color: "primary.main",
            }}
          >
            Shift Completed Today
          </Button>
        )}

        {/* Verification Footer Note */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.75,
            pt: 1,
          }}
        >
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              bgcolor: isDark ? alpha("#10b981", 0.2) : "#dcfce7",
              color: "#059669",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MdCheckCircle size={12} />
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: "0.74rem",
              fontWeight: 500,
            }}
          >
            Verified via Office Network (Secure Attendance Session)
          </Typography>
        </Box>

        {canViewAttendanceRecords && (
          <Button
            variant="text"
            fullWidth
            size="small"
            endIcon={<MdArrowForward size={14} />}
            onClick={() => navigate("/company/attendance-records")}
            sx={{
              mt: 0.5,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.74rem",
              color: "text.secondary",
              "&:hover": { color: "primary.main" },
            }}
          >
            View Attendance History
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default MyAttendanceCard;
